import shouldOfferInstallPrompt from '@Application/ApplicationSettings/InstallPrompt/shouldOfferInstallPrompt';
import readInstallPromptDismissal from '@Application/ApplicationSettings/InstallPrompt/readInstallPromptDismissal';
import recordInstallPromptDismissal from '@Application/ApplicationSettings/InstallPrompt/recordInstallPromptDismissal';
import useHasCoarsePointer from '@UI/Guidelines/Shell/InstallAppBanner/Hooks/useHasCoarsePointer';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import useInstallPrompt from '@UI/Providers/InstallPromptProvider/useInstallPrompt';
import { css } from '@emotion/react';
import { Download } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, Message } from '@verclam/ui-kit';

const messageStyle = css`
	margin: 0 auto;
	display: flex;
	width: fit-content;
`;

const contentStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const textStyle = css`
	flex: 1 1 auto;
`;

const actionsStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const actionButtonStyle = css`
	min-height: 44px;
`;

/**
 * Masked when the browser has not offered `beforeinstallprompt` — which also covers an
 * already-installed app, since the browser withholds the event in that case — when the visitor is
 * not signed in, or while `shouldOfferInstallPrompt` deems the last refusal too recent. Visible
 * otherwise. Reproposes after an escalating delay following each of the first four refusals, and
 * never again spontaneously past the fifth — see `@Application/InstallPrompt/shouldOfferInstallPrompt`
 * for the exact table.
 */
const InstallAppBanner: React.FC = () => {
	const { isAvailable, promptInstall } = useInstallPrompt();
	const { sessionStatus } = useAuthenticatedUser();
	const dispatch = useDispatch();
	const hasCoarsePointer = useHasCoarsePointer();
	const [isEligibleForOffer, setIsEligibleForOffer] = React.useState<boolean>(() =>
		shouldOfferInstallPrompt(readInstallPromptDismissal(), new Date()),
	);

	const handleInstall = React.useCallback((): void => {
		promptInstall().catch(() => {
			// A rejected/dismissed prompt leaves the banner as it was; nothing to report to the user.
		});
	}, [promptInstall]);

	const handleDismiss = React.useCallback((): void => {
		dispatch(recordInstallPromptDismissal());
		setIsEligibleForOffer(false);
	}, [dispatch]);

	const isVisible = isAvailable && sessionStatus === 'authenticated' && isEligibleForOffer;

	if (!isVisible) {
		return null;
	}

	return (
		<Message
			severity="info"
			css={messageStyle}
			content={
				<div css={contentStyle}>
					<Download size={20} aria-hidden="true" />
					<span css={textStyle}>
						{hasCoarsePointer
							? 'Installer Dixit sur votre téléphone'
							: 'Installer Dixit sur votre ordinateur'}
					</span>
					<div css={actionsStyle}>
						<Button
							type="button"
							label="Installer"
							css={actionButtonStyle}
							onClick={handleInstall}
						/>
						<Button
							type="button"
							label="Non merci"
							severity="secondary"
							css={actionButtonStyle}
							onClick={handleDismiss}
						/>
					</div>
				</div>
			}
		/>
	);
};

export default InstallAppBanner;
